import { Fragment } from "react";
import { ArrowRight, Check } from "lucide-react";
import { Area, AreaChart, Cell, Pie, PieChart, ResponsiveContainer, Tooltip, XAxis } from "recharts";

// Presentation-only premium Home modules. No business logic, no data fetching —
// every value is passed in from DashboardHome's existing data. Palette is locked
// to the TPE system (deep green / emerald / sage / champagne / ivory).
const GREEN = "#164C3B";
const EMERALD = "#2F6B57";
const CHAMPAGNE = "#C9A86A";

const money = (n) => "$" + Math.round(Number(n || 0)).toLocaleString();
const shortDate = (s) => {
  try { return new Date(s).toLocaleDateString(undefined, { month: "short", day: "numeric" }); }
  catch { return ""; }
};

export function HeroSummary({ onFeelingRich, lifetime }) {
  const hasStory = lifetime && lifetime.money_put_to_work > 0;
  return (
    <section className="mb-6 overflow-hidden rounded-3xl border border-[#E7E0D3] bg-gradient-to-br from-[#FCFBF8] to-[#F2EFE6] lg:mb-8" data-testid="home-hero">
      <div className="flex flex-col gap-6 p-6 sm:p-8 lg:flex-row lg:items-center lg:justify-between">
        <div className="min-w-0">
          <p className="pe-eyebrow text-[#2F6B57]">The Paycheck Edit</p>
          <h1 className="mt-2 font-serif text-3xl leading-tight text-[#171717] sm:text-[2.6rem]">Feeling rich?</h1>
          <p className="mt-2 max-w-md text-[15px] leading-relaxed text-[#525252]">
            Tell us what came in and we'll show you what it can do. Nothing moves until you confirm it.
          </p>
          <button type="button" onClick={onFeelingRich} data-testid="home-feeling-rich"
            className="group mt-5 inline-flex min-h-[3.25rem] items-center justify-center gap-2.5 rounded-full bg-[#164C3B] px-7 text-[15px] font-semibold uppercase tracking-[0.05em] text-white shadow-[0_16px_40px_-18px_rgba(22,76,59,0.6)] transition-all duration-200 hover:bg-[#0f3528]">
            Feeling rich? 💰 <ArrowRight className="h-4 w-4 transition-transform duration-200 group-hover:translate-x-0.5" />
          </button>
        </div>
        {hasStory && (
          <div className="shrink-0 rounded-2xl bg-[#164C3B] px-6 py-5 text-white" data-testid="home-hero-stat">
            <p className="text-[11px] font-semibold uppercase tracking-[0.16em] text-[#C9A86A]">Put to work</p>
            <p className="mt-1 font-serif text-4xl tabular-nums">{money(lifetime.money_put_to_work)}</p>
            <p className="mt-1 text-xs text-white/70">
              across {lifetime.confirmed_sprints || 0} confirmed {(lifetime.confirmed_sprints || 0) === 1 ? "Edit" : "Edits"}
            </p>
          </div>
        )}
      </div>
    </section>
  );
}

const STAGES = [["protect", "Protect"], ["eliminate", "Eliminate"], ["build", "Build"], ["freedom", "Freedom"]];
function stageIndex(phase) {
  const n = (phase || "").toLowerCase();
  if (n === "protect" || n === "protection") return 0;
  if (n === "eliminate" || n === "debt") return 1;
  if (n === "build") return 2;
  if (n === "build_unassigned") return 2;
  if (n === "freedom") return 3;
  return -1;
}

export function StageMap({ phase }) {
  const active = stageIndex(phase);
  return (
    <section className="mb-6 rounded-2xl border border-[#ECE6DC] bg-[#FCFBF8] p-5 sm:p-6" data-testid="home-stage-map">
      <p className="pe-eyebrow text-[#9A7B3A]">Your path</p>
      <div className="mt-3 grid grid-cols-4 gap-1.5 sm:gap-2">
        {STAGES.map(([k, label], i) => {
          const isActive = i === active;
          const done = active > -1 && i < active;
          return (
            <div key={k} data-testid={`home-stage-${k}`}
              className={`rounded-xl px-1.5 py-3 text-center transition-colors ${isActive ? "bg-[#164C3B] text-white" : done ? "bg-[#DDE7DF] text-[#164C3B]" : "bg-[#F3EFE7] text-[#A89A88]"}`}>
              <p className="text-[11px] font-semibold uppercase tracking-[0.04em] sm:text-xs">{label}</p>
              {isActive ? (
                <p className="mt-0.5 text-[10px] font-medium text-[#C9A86A]">You're here</p>
              ) : done ? (
                <p className="mt-0.5 text-[10px] font-medium text-[#2F6B57]">Done ✓</p>
              ) : (
                <p className="mt-0.5 text-[10px] text-[#C4B8A6]">Next</p>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

// Slim path stepper. Desktop shows Protect -> Eliminate -> Build -> Freedom with
// labels; mobile shows "Stage N of 4 + active label" over a 4-segment line (NO
// truncated stage names). Presentation only.
export function SlimStepper({ phase }) {
  const active = stageIndex(phase);
  const activeLabel = active > -1 ? STAGES[active][1] : null;
  return (
    <div data-testid="home-stepper" className="rounded-xl border border-[#ECE6DC] bg-[#FCFBF8] px-4 py-3 sm:px-5">
      <p className="mb-2 text-[11px] font-semibold uppercase tracking-[0.16em] text-[#164C3B] sm:hidden">
        {active > -1 ? `Stage ${active + 1} of 4 · ${activeLabel}` : "Your path"}
      </p>
      <div className="flex items-center gap-2 sm:gap-3">
        {STAGES.map(([k, label], i) => {
          const isActive = i === active;
          const done = active > -1 && i < active;
          return (
            <Fragment key={k}>
              <div data-testid={`home-stage-${k}`} className="flex min-w-0 items-center gap-1.5">
                <span className={`flex h-5 w-5 shrink-0 items-center justify-center rounded-full text-[10px] font-semibold ${
                  done ? "bg-[#2F6B57] text-white" : isActive ? "bg-[#164C3B] text-white" : "bg-[#EDE7DC] text-[#A89A88]"
                }`}>
                  {done ? <Check className="h-3 w-3" /> : i + 1}
                </span>
                <span className={`hidden truncate text-xs font-semibold sm:inline ${isActive ? "text-[#164C3B]" : done ? "text-[#2F6B57]" : "text-[#A89A88]"}`}>{label}</span>
              </div>
              {i < STAGES.length - 1 && <span className={`h-0.5 flex-1 rounded-full ${done ? "bg-[#2F6B57]" : "bg-[#ECE6DC]"}`} aria-hidden="true" />}
            </Fragment>
          );
        })}
      </div>
    </div>
  );
}

function ChartCard({ title, sub, children, testid }) {
  return (
    <div className="rounded-2xl border border-[#ECE6DC] bg-white p-5 sm:p-6" data-testid={testid}>
      <p className="text-xs font-semibold uppercase tracking-[0.14em] text-[#9A7B3A]">{title}</p>
      {sub && <p className="mt-0.5 text-sm text-[#70584A]">{sub}</p>}
      <div className="mt-4">{children}</div>
    </div>
  );
}

export function ProgressCharts({ prog }) {
  const lifetime = prog?.lifetime || {};
  const histRaw = prog?.sprint_history || [];
  // sprint_history is newest-first in the app; chronological for a cumulative line.
  const hist = histRaw.slice().reverse();
  let run = 0;
  const cumData = hist.map((h) => {
    run += Number(h.amount_confirmed || 0);
    return { date: shortDate(h.date), value: Math.round(run) };
  });
  const startingDebt = Number(prog?.starting_debt || 0);
  const currentDebt = Number(prog?.current_debt || 0);
  const dist = [
    { name: "Protect", value: Math.round(Number(lifetime.protected || 0)), color: EMERALD },
    { name: "Eliminate", value: Math.round(Number(lifetime.debt_attacked || 0)), color: GREEN },
    { name: "Build", value: Math.round(Number(lifetime.goals_built || 0)), color: CHAMPAGNE },
  ].filter((d) => d.value > 0);

  const hasCum = cumData.length >= 2;
  const hasDebt = startingDebt > 0;
  const hasDist = dist.length > 0;
  if (!hasCum && !hasDebt && !hasDist) return null;

  const paidPct = hasDebt ? Math.min(100, Math.max(0, Math.round(((startingDebt - currentDebt) / startingDebt) * 100))) : 0;
  const distTotal = dist.reduce((s, d) => s + d.value, 0);

  return (
    <section className="mt-6" data-testid="home-progress-charts">
      <div className="mb-4 flex items-end justify-between">
        <h2 className="font-serif text-2xl text-[#171717]">Your confirmed progress</h2>
        <span className="text-xs font-medium uppercase tracking-[0.12em] text-[#A89A88]">The Receipts</span>
      </div>
      <div className="grid gap-4 lg:grid-cols-2">
        {hasCum && (
          <ChartCard title="Money put to work" sub="Cumulative confirmed dollars over time" testid="chart-cumulative">
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={cumData} margin={{ top: 5, right: 8, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="cumFill" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor={EMERALD} stopOpacity={0.35} />
                    <stop offset="100%" stopColor={EMERALD} stopOpacity={0.02} />
                  </linearGradient>
                </defs>
                <XAxis dataKey="date" tick={{ fontSize: 11, fill: "#A89A88" }} tickLine={false} axisLine={false} minTickGap={24} />
                <Tooltip formatter={(v) => [money(v), "Put to work"]} contentStyle={{ borderRadius: 12, border: "1px solid #ECE6DC", fontSize: 13 }} />
                <Area type="monotone" dataKey="value" stroke={GREEN} strokeWidth={2.5} fill="url(#cumFill)" />
              </AreaChart>
            </ResponsiveContainer>
          </ChartCard>
        )}

        {hasDebt && (
          <ChartCard title="Debt eliminated" sub={`${money(startingDebt - currentDebt)} of ${money(startingDebt)} attacked`} testid="chart-debt">
            <div className="flex items-baseline justify-between">
              <p className="font-serif text-4xl tabular-nums text-[#164C3B]" data-testid="chart-debt-pct">{paidPct}%</p>
              <p className="text-sm text-[#70584A]">{money(currentDebt)} remaining</p>
            </div>
            <div className="mt-3 h-3 w-full overflow-hidden rounded-full bg-[#EDE7DC]" role="img" aria-label={`${paidPct}% of starting debt eliminated`}>
              <div className="h-full rounded-full bg-[#2F6B57] transition-all" style={{ width: `${paidPct}%` }} />
            </div>
            <div className="mt-3 flex justify-between text-xs text-[#A89A88]">
              <span>Start {money(startingDebt)}</span>
              <span>{(prog?.debts_eliminated || 0)} eliminated</span>
            </div>
          </ChartCard>
        )}

        {hasDist && (
          <ChartCard title="Where it went" sub="Confirmed moves across your path" testid="chart-distribution">
            <div className="flex items-center gap-5">
              <ResponsiveContainer width={140} height={140}>
                <PieChart>
                  <Pie data={dist} dataKey="value" nameKey="name" innerRadius={42} outerRadius={64} paddingAngle={2} stroke="none">
                    {dist.map((d) => <Cell key={d.name} fill={d.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n) => [money(v), n]} contentStyle={{ borderRadius: 12, border: "1px solid #ECE6DC", fontSize: 13 }} />
                </PieChart>
              </ResponsiveContainer>
              <ul className="flex-1 space-y-2">
                {dist.map((d) => (
                  <li key={d.name} className="flex items-center justify-between text-sm">
                    <span className="flex items-center gap-2 text-[#525252]">
                      <span className="inline-block h-2.5 w-2.5 rounded-full" style={{ backgroundColor: d.color }} /> {d.name}
                    </span>
                    <span className="font-medium tabular-nums text-[#171717]">
                      {distTotal ? Math.round((d.value / distTotal) * 100) : 0}%
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </ChartCard>
        )}
      </div>
    </section>
  );
}
